/* Video library: card selection swaps the featured player.
   Each card carries an inert <template> of its own player markup, so a swap costs no request and
   the templates' poster images are never fetched until one is cloned into the stage. */
class VideoLibrary extends HTMLElement {
  static scrollOffset = 20;

  connectedCallback() {
    this.stage = this.querySelector('[data-video-stage]');
    this.featured = this.querySelector('[data-video-featured]');
    this.panel = {
      label: this.querySelector('[data-video-panel-label]'),
      title: this.querySelector('[data-video-panel-title]'),
      text: this.querySelector('[data-video-panel-text]'),
    };

    if (!this.stage) return;

    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const card = event.target.closest('[data-video-card]');
    if (!card || !this.contains(card)) return;

    this.select(card);
  }

  select(card) {
    const template = card.querySelector('[data-video-stage-template]');
    if (!template) return;

    this.stage.replaceChildren(template.content.cloneNode(true));

    Object.entries(this.panel).forEach(([key, element]) => {
      if (!element) return;
      const value = card.dataset[`video${key.charAt(0).toUpperCase()}${key.slice(1)}`] || '';
      element.textContent = value;
      element.hidden = value === '';
    });

    this.querySelectorAll('[data-video-card][aria-current]').forEach((selected) => {
      selected.removeAttribute('aria-current');
    });
    card.setAttribute('aria-current', 'true');

    this.scrollToPlayer();
    this.stage.focus({ preventScroll: true });
    this.stage.querySelector('video-element')?.play();
  }

  /* Measures the sticky header the way the theme's own tab scrolling does, rather than reading
     --header-height: that variable is only ever set by the basic-header variant, so it is empty
     on every page using the sticky header and the player lands underneath it. */
  get headerHeight() {
    const header = document.querySelector('.header-section header[is="sticky-header"]');

    return header ? Math.round(header.offsetHeight) : 0;
  }

  /* Leaves the page alone when the player is already fully in view, so only a card chosen from
     below the fold moves anything. */
  scrollToPlayer() {
    const target = this.featured || this.stage;
    const bounds = target.getBoundingClientRect();
    const safeTop = this.headerHeight + VideoLibrary.scrollOffset;

    if (bounds.top >= safeTop && bounds.bottom <= window.innerHeight) return;

    window.scrollTo({ top: Math.max(0, window.scrollY + bounds.top - safeTop) });
  }
}

customElements.define('video-library', VideoLibrary);
